// First party page view tracking for the public marketing pages.
//
// Deliberately tiny and deliberately silent. It must never throw, never block
// a render, and never run on an authenticated app route.
//
// The session id lives in sessionStorage, not a cookie, so it disappears when
// the visitor closes the tab. Nothing identifying is sent: just the path and
// the referrer the browser already knows about.

const SESSION_KEY = 'as_sid';

// Explicit allowlist. An allowlist rather than a blocklist so that any new
// authenticated route added later cannot accidentally start reporting.
const MARKETING_PATHS = [
  '/',
  '/fbr-digital-invoicing',
  '/pra-digital-invoicing',
  '/tax-filing',
  '/odoo-integration',
];

// Static guide pages are served outside React Router, but keep the prefix here
// so the same helper can judge them.
const MARKETING_PREFIXES = ['/guides'];

const normalisePath = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  let path = raw.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) return null;
  if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '') || '/';
  return path;
};

export const isMarketingPath = (raw) => {
  const path = normalisePath(raw);
  if (!path) return false;
  if (MARKETING_PATHS.indexOf(path) !== -1) return true;
  return MARKETING_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
};

const randomId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID().replace(/-/g, '');
    }
    if (window.crypto && window.crypto.getRandomValues) {
      const buf = new Uint8Array(16);
      window.crypto.getRandomValues(buf);
      return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {
    // fall through to the non crypto path below
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

export const getSessionId = () => {
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid || !/^[A-Za-z0-9_-]{8,64}$/.test(sid)) {
      sid = randomId();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    // Private mode or storage disabled. Fall back to a per load id so the
    // visit still counts as one session for this page load.
    return randomId();
  }
};

// Guards against the same path being reported twice in a row, which React
// StrictMode double effects and rapid re-renders would otherwise cause.
let lastReported = null;
let lastReportedAt = 0;

export const trackPageView = (rawPath) => {
  try {
    const path = normalisePath(rawPath);
    if (!path || !isMarketingPath(path)) return;

    const now = Date.now();
    if (path === lastReported && now - lastReportedAt < 1000) return;
    lastReported = path;
    lastReportedAt = now;

    const body = JSON.stringify({
      path,
      referrer: document.referrer || null,
      session_id: getSessionId(),
    });

    // keepalive so a hit fired just before a navigation still goes out.
    // No await anywhere: the caller is never held up and errors are dropped.
    fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (e) {
    // Analytics must never break a page.
  }
};

const siteAnalytics = { trackPageView, isMarketingPath, getSessionId };

export default siteAnalytics;
